import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import {
  AppBar,
  Avatar,
  Box,
  Card,
  IconButton,
  Modal,
  Paper,
  PaperProps,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import SendIcon from "@mui/icons-material/Send";
import TkButton from "../../../components/atoms/TkButton";
import TkTextArea from "../../../components/molecules/TkTextArea";
import TkDownloadLink from "../../../components/atoms/TkDownloadLink";
import { FileToBase64encode } from "../../../utils/TkFileEncode";
import { schema } from "./parts";
import "./_style.scss";
import { PostKbn } from "../../../assets/data/Constants";

/**
 * ダイアログ内の要素
 * @return Chats
 */
export const Chats = (props: any) => {
  const { chats, onChange } = props;
  const { handleSubmit, control, reset } = useForm({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      message: "",
    },
  });

  const [value, setValue] = useState("");
  const [disable, setDisable] = useState(true);

  const handleSend = (data: any) => {
    onChange(data);

    setValue("");
    setDisable(true);
    reset();
  };

  const handleChange = (value: any, error: any) => {
    setValue(value);
    setDisable(!value);
  };

  const handleFileChange = async (e: any) => {
    const encodeResult = await FileToBase64encode(e.target.files[0]);
    const file = {
      file: {
        fileName: encodeResult.filename,
        fileBase64: encodeResult.base64result,
      },
    };

    onChange(file);
  };

  const messagesEndRef = useRef<HTMLElement>(null);

  // 自動スクロール
  useEffect(() => {
    const scrollHeight: number = messagesEndRef.current!.scrollHeight;
    const height: number = messagesEndRef.current!.clientHeight;
    const maxScrollTop: number = scrollHeight - height;

    messagesEndRef.current!.scrollTop = maxScrollTop;
  }, [chats]);

  return (
    <Box className="chat" ref={messagesEndRef} data-testid="chat">
      <Box className="chat-show">
        {[...chats]
          .sort((a, b) => a.chatHistoryNo - b.chatHistoryNo)
          .map((i: any, k: any) => (
            <React.Fragment key={k}>
              <Box
                className={
                  i.kbn === PostKbn["機構"] ? `kiko-box` : `keiyakusya-box`
                }
              >
                {i.kbn === PostKbn["機構"] ? (
                  <Avatar className="avatarBot">機</Avatar>
                ) : (
                  <Avatar className="avatarKei">契</Avatar>
                )}

                <Box
                  className={i.kbn === PostKbn["機構"] ? `kiko` : `keiyakusya`}
                >
                  {i.detail ? (
                    <Card
                      variant="outlined"
                      className={
                        i.kbn === PostKbn["機構"]
                          ? `kiko-card`
                          : `keiyakusya-card`
                      }
                    >
                      <Box className="chatDetail">
                        <Typography
                          textAlign="left"
                          sx={{ whiteSpace: "pre-line" }}
                        >
                          {i.detail}
                        </Typography>
                      </Box>
                    </Card>
                  ) : i.fileName ? (
                    <Box className="file">
                      <Box className="image" />
                      <TkDownloadLink
                        dataname={i.fileName}
                        base64filedata={i.fileBase64}
                        downloadBase64={true}
                      />
                    </Box>
                  ) : null}
                  <Typography>{i.date}</Typography>
                </Box>
              </Box>
            </React.Fragment>
          ))}
      </Box>

      <Box
        className="form"
        component="form"
        onSubmit={handleSubmit(handleSend)}
      >
        <Controller
          name="message"
          control={control}
          rules={schema}
          render={({ field, formState: { errors } }) => (
            <TkTextArea
              {...field}
              multiline
              fullWidth
              wantToSetValue={value}
              inputProps={{ maxLength: 85 }}
              error={!!errors.message}
              helperText={errors.message?.message as string}
              onGetInputValue={(e: any) => handleChange(e, !!errors.message)}
            />
          )}
        />
        <Box className="sendButton">
          <input
            id="file-upload"
            type="file"
            onChange={handleFileChange}
            style={{ display: "none" }}
            data-testid="file-upload"
          />
          <label htmlFor="file-upload">
            <IconButton component="span">
              <AttachFileOutlinedIcon sx={{ cursor: "pointer" }} />
            </IconButton>
          </label>

          <TkButton type="submit" endIcon={<SendIcon />} disabled={disable}>
            送信
          </TkButton>
        </Box>
      </Box>
    </Box>
  );
};

const DraggableComponent: React.FC = React.forwardRef<
  HTMLDivElement,
  PaperProps
>((props, ref) => {
  const [position, setPosition] = useState({
    x: window.innerWidth - 525,
    y: window.innerHeight - 500,
  });
  const [size, setSize] = useState({ width: 525, height: 500 });
  const draggableRef = useRef<HTMLDivElement>(null);
  const resizing = useRef(false);
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const resizeOffset = useRef({ width: 0, height: 0 });

  useImperativeHandle(ref, () => draggableRef.current!);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!draggableRef.current) return;

      if (dragging.current) {
        const newX = event.clientX - offset.current.x;
        const newY = event.clientY - offset.current.y;

        const maxX = window.innerWidth - size.width;
        const maxY = window.innerHeight - size.height;

        const clampedX = Math.max(0, Math.min(newX, maxX));
        const clampedY = Math.max(0, Math.min(newY, maxY));

        setPosition({ x: clampedX, y: clampedY });
      } else if (resizing.current) {
        const minWidth = 400;
        const minHeight = 400;
        const maxWidth = Math.min(1000, window.innerWidth - position.x);
        const maxHeight = Math.min(800, window.innerHeight - position.y);

        const newWidth = Math.max(
          minWidth,
          Math.min(
            maxWidth,
            event.clientX -
              draggableRef.current.getBoundingClientRect().left +
              resizeOffset.current.width
          )
        );
        const newHeight = Math.max(
          minHeight,
          Math.min(
            maxHeight,
            event.clientY -
              draggableRef.current.getBoundingClientRect().top +
              resizeOffset.current.height
          )
        );

        setSize({ width: newWidth, height: newHeight });
      }
    },
    [position, size]
  );

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove]);

  const handleMouseDown = (event: React.MouseEvent) => {
    if (
      draggableRef.current &&
      event.clientY - draggableRef.current.getBoundingClientRect().top < 48
    ) {
      event.preventDefault();
      dragging.current = true;
      offset.current = {
        x: event.clientX - position.x,
        y: event.clientY - position.y,
      };
    }
  };

  const handleMouseUp = () => {
    if (dragging.current) {
      dragging.current = false;
    }
    if (resizing.current) {
      resizing.current = false;
    }
  };

  return (
    <Box
      ref={draggableRef}
      onMouseDown={handleMouseDown}
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
      }}
    >
      <Paper {...props} ref={ref} />
    </Box>
  );
});
/**
 * チャットボット用ダイアログ
 * @return ChatDialog
 */
export const ChatDialog = (props: any) => {
  const { open, content, onClose } = props;
  const [isOrigin, setIsOrigin] = useState(true);

  return (
    <Modal
      className="SCRC090002Modal"
      open={open}
      hideBackdrop
      disableScrollLock={true}
      component={DraggableComponent}
      aria-labelledby="draggable-modal-title"
      sx={{
        top: "inherit",
        left: "inherit",
      }}
      data-testid="chatDialog"
    >
      <>
        <AppBar position="static" id="draggable-modal-title" className="appbar">
          <Toolbar variant="dense" className="toolbar">
            <Typography>機構への連絡掲示板</Typography>
            <Box className="headerButton">
              <IconButton
                aria-label="cropLandscape"
                size="small"
                onClick={() => setIsOrigin(!isOrigin)}
              >
                {isOrigin ? <RemoveIcon /> : <CropSquareIcon />}
              </IconButton>
              <IconButton aria-label="delete" size="small" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
        {isOrigin ? content : null}
      </>
    </Modal>
  );
};
